import { Group, type PerspectiveCamera } from 'three';
import { MirrorDriftController, type MirrorDriftOptions } from './MirrorDriftController.js';
import { MirrorShard } from './MirrorShard.js';
import {
  resolveMirrorVideoCandidates,
  type MirrorVideoCandidate,
  type MirrorVideoRequest,
} from './MirrorVideoResolver.js';

/**
 * Mirror Drift: fragments of the song's music video adrift behind the play space.
 *
 * Ordering is a hard constraint rather than a preference. The shards render at
 * `renderOrder` 8, between the structural rings (5) and the mandala (10), and the
 * mandala and ripples are additive. A shard can therefore drift in front of the
 * mandala without ever occluding it — the mandala's light simply adds over the
 * glass. "Must not interfere with the mandala" is satisfied structurally, not by
 * hoping the drift keeps away.
 *
 * A missing or undecodable video disables this view and nothing else. The world
 * is expected to run without an MV, so failure here is a `console.warn` and an
 * empty group, never an exception.
 */

export interface MirrorDriftOptionsFull extends MirrorDriftOptions {
  readonly shardCount?: number;
  readonly seed?: number;
  readonly fieldRadius?: number;
  /**
   * Whether `load()` should begin playback as soon as the video decodes.
   *
   * Set false when something else owns the moment of starting — the runtime
   * starts the song and the mirrors inside one gesture handler so they share an
   * origin, and a mirror that began playing during loading would already be
   * seconds ahead of the music.
   */
  readonly autoplay?: boolean;
}

export type MirrorDriftStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'awaiting-gesture'
  | 'unavailable';

export interface MirrorDriftDiagnostics {
  readonly status: MirrorDriftStatus;
  readonly url: string | null;
  readonly origin: MirrorVideoCandidate['origin'] | null;
  readonly shardCount: number;
  readonly activeCount: number;
  readonly readyCount: number;
  readonly playingCount: number;
  readonly reason: string | null;
  readonly videoSize: { width: number; height: number } | null;
  readonly durationSeconds: number;
  readonly playback: ReadonlyArray<{ rate: number; offset: number; currentTime: number }>;
  readonly decoded: { total: number; dropped: number } | null;
}

export class MirrorDriftView {
  readonly object = new Group();
  readonly shards: readonly MirrorShard[];

  private readonly controller: MirrorDriftController;
  private status: MirrorDriftStatus = 'idle';
  private resolvedUrl: string | null = null;
  private resolvedOrigin: MirrorVideoCandidate['origin'] | null = null;
  private failureReason: string | null = null;
  private activeCount: number;
  private gestureHandler: (() => void) | null = null;
  private disposed = false;
  private readonly autoplay: boolean;

  constructor(options: MirrorDriftOptionsFull = {}) {
    const shardCount = Math.max(1, Math.min(options.shardCount ?? 4, 12));
    const fieldRadius = options.fieldRadius ?? 9;
    const seed = options.seed ?? 0x4d1a;

    this.object.name = 'MirrorDrift';
    this.shards = Array.from(
      { length: shardCount },
      (_, index) => new MirrorShard({ seed, index, fieldRadius }),
    );
    this.activeCount = shardCount;
    this.autoplay = options.autoplay ?? true;
    this.controller = new MirrorDriftController(options);
  }

  get enabled(): boolean {
    return this.status === 'ready' || this.status === 'awaiting-gesture';
  }

  /**
   * Resolves the MV and loads it into every shard.
   *
   * Always resolves; never rejects. Returns whether Mirror Drift became active.
   */
  async load(request: MirrorVideoRequest): Promise<boolean> {
    if (this.disposed) return false;
    // Whatever was showing is let go first. Without this a second `load` would
    // leave the previous song's decoder running and its texture allocated, and
    // — worse — a source disposed by an earlier `disable()` could never be
    // reloaded, so a song with no MV would permanently disable Mirror Drift for
    // every song after it.
    this.unload();
    this.status = 'loading';

    const candidates = resolveMirrorVideoCandidates(request);
    if (candidates.length === 0) {
      return this.disable(`no MV could be derived from displayName "${request.displayName}"`);
    }

    // Probe with the first shard only. A failing URL would otherwise spin up one
    // doomed decoder per shard.
    const lead = this.shards[0];
    if (lead === undefined) return this.disable('no shards were created');

    let chosen: MirrorVideoCandidate | null = null;
    for (const candidate of candidates) {
      if (await lead.video.load(candidate.url)) {
        chosen = candidate;
        break;
      }
    }
    if (chosen === null) {
      return this.disable(
        `none of the candidate URLs could be loaded: ${candidates.map((c) => c.url).join(', ')}`,
      );
    }

    this.resolvedUrl = chosen.url;
    this.resolvedOrigin = chosen.origin;
    lead.attachVideo();

    // The rest share the URL, so the HTTP cache serves one download — but each
    // still allocates its own decoder, which is the cost this phase measures.
    const rest = this.shards.slice(1);
    const loaded = await Promise.all(rest.map((shard) => shard.video.load(chosen.url)));
    rest.forEach((shard, index) => {
      if (loaded[index] === true) shard.attachVideo();
    });

    const ready = this.shards.filter((shard) => shard.video.state === 'ready');
    if (ready.length === 0) return this.disable('no shard could decode the MV');

    for (const shard of ready) this.object.add(shard.mesh);
    this.status = 'ready';
    // Shards are already bound to their first decoded frame, so the glass is
    // visible whether or not playback has begun.
    if (this.autoplay) await this.startPlayback();
    return true;
  }

  /**
   * Begins playback on every ready shard.
   *
   * Public so a caller that also owns the audio can start both in one gesture.
   */
  async play(): Promise<void> {
    if (this.status === 'unavailable') return;
    await this.startPlayback();
  }

  /** Starts every ready decoder, arranging a gesture retry if autoplay is blocked. */
  private async startPlayback(): Promise<void> {
    const ready = this.shards.filter((shard) => shard.video.state === 'ready');
    await Promise.all(ready.map((shard) => shard.video.play()));
    // `play()` resolving does not mean `paused` has flipped yet; let the elements
    // settle before judging, or a healthy start is misreported as blocked.
    await new Promise((resolve) => window.setTimeout(resolve, 150));

    // Trust the element's own state, not the promise. `play()` rejects with
    // AbortError when a still-settling `load()` interrupts it, even though
    // playback then starts normally a moment later — believing the promise
    // reported "blocked by autoplay policy" on a video that was already running.
    if (ready.every((shard) => shard.video.playing)) {
      this.status = 'ready';
      this.clearGestureHandler();
      return;
    }
    // Muted inline video is normally exempt from autoplay policy, but not on
    // every platform; the world keeps running either way and the shards simply
    // hold their first frame until the user touches something.
    this.status = 'awaiting-gesture';
    if (this.gestureHandler !== null) return;
    const handler = () => {
      void this.startPlayback();
    };
    this.gestureHandler = handler;
    window.addEventListener('pointerdown', handler, { once: true });
    window.addEventListener('keydown', handler, { once: true });
  }

  private clearGestureHandler(): void {
    if (this.gestureHandler === null) return;
    window.removeEventListener('pointerdown', this.gestureHandler);
    window.removeEventListener('keydown', this.gestureHandler);
    this.gestureHandler = null;
  }

  /**
   * Lets go of the current MV without destroying anything reusable.
   *
   * Public because the runtime has to release the decoders *before* it revokes
   * the object URL they were reading from: revoking a URL a `<video>` still
   * holds open is a stale handle in the browser's own bookkeeping.
   */
  unload(): void {
    for (const shard of this.shards) shard.video.release();
    this.object.clear();
    this.resolvedUrl = null;
    this.resolvedOrigin = null;
    this.failureReason = null;
    if (this.status !== 'idle') this.status = 'idle';
  }

  private disable(reason: string): false {
    this.unload();
    this.status = 'unavailable';
    this.failureReason = reason;
    console.warn(`[MirrorDrift] disabled: ${reason}`);
    return false;
  }

  /**
   * Limits how many shards are drawn and decoded.
   *
   * Exists so the 4-versus-6 cost can be measured on one running scene rather
   * than across two page loads with different GPU states.
   */
  setActiveCount(count: number): void {
    const clamped = Math.max(0, Math.min(count, this.shards.length));
    this.activeCount = clamped;
    this.shards.forEach((shard, index) => {
      const active = index < clamped;
      shard.mesh.visible = active;
      if (active) {
        if (this.status === 'ready') void shard.video.play();
      } else {
        shard.video.pause();
      }
    });
  }

  update(delta: number, time: number, camera: PerspectiveCamera): void {
    if (!this.enabled) return;
    const active = this.shards.slice(0, this.activeCount);
    for (const shard of active) shard.video.refresh();
    this.controller.update(active, delta, time, camera);
  }

  diagnostics(): MirrorDriftDiagnostics {
    const lead = this.shards[0];
    const element = lead?.video.element;
    let total = 0;
    let dropped = 0;
    let sawQuality = false;
    for (const shard of this.shards) {
      const quality = shard.video.quality();
      if (quality === null) continue;
      sawQuality = true;
      total += quality.total;
      dropped += quality.dropped;
    }

    return {
      status: this.status,
      url: this.resolvedUrl,
      origin: this.resolvedOrigin,
      shardCount: this.shards.length,
      activeCount: this.activeCount,
      readyCount: this.shards.filter((shard) => shard.video.state === 'ready').length,
      playingCount: this.shards.filter((shard) => shard.video.playing).length,
      reason: this.failureReason,
      videoSize:
        element !== undefined && element.videoWidth > 0
          ? { width: element.videoWidth, height: element.videoHeight }
          : null,
      durationSeconds: lead?.video.duration ?? 0,
      playback: this.shards.map((shard) => ({
        rate: shard.playback.playbackRate,
        offset: shard.playback.offsetSeconds,
        currentTime: shard.video.element.currentTime,
      })),
      decoded: sawQuality ? { total, dropped } : null,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearGestureHandler();
    for (const shard of this.shards) shard.dispose();
    this.object.clear();
  }
}
